// Production Schedule — closed-set enums and capacity constants.
// Shared by server (Zod, CHECK constraints) and client (filter UIs).

// Seed labels for production_job_statuses. The runtime source of
// truth is the API (GET /api/tools/production-schedule/statuses) —
// the admin can add/rename/archive at runtime — but this seed list
// still drives the type-narrowed STATUS_COLORS fallback and the
// initial client render before the catalogue fetch completes. New
// custom statuses created via the admin UI widen JobStatus to a
// free string at runtime.
export const JOB_STATUSES = [
  "New",
  "Scheduled",
  "In Progress",
  "Review",
  "IFA",
  "Complete",
  "Cancelled",
] as const;
export type JobStatus = string;

export const JOB_PRIORITIES = ["Urgent", "High", "Normal", "Low"] as const;
export type JobPriority = (typeof JOB_PRIORITIES)[number];

export const SPOOL_STATUSES = [
  "pending",
  "cutting",
  "cut",
  "welding",
  "complete",
] as const;
export type SpoolStatus = (typeof SPOOL_STATUSES)[number];

export const GROUP_BYS = ["status", "type", "assignee", "priority"] as const;
export type GroupBy = (typeof GROUP_BYS)[number];

export const GANTT_ZOOMS = ["day", "week", "month"] as const;
export type GanttZoom = (typeof GANTT_ZOOMS)[number];

// Mockup hard-codes 80h per bay/week (production-schedule.html line ~861).
// Centralised here so the bay capacity view and any future bay-settings
// admin both reference the same constant.
export const BAY_HOURLY_CAPACITY = 80;
export const BAYS = [1, 2, 3, 4, 5, 6, 7, 8] as const;
export type Bay = (typeof BAYS)[number];

// Default solution types seeded by migration 0005. Listed here so the
// client can prefill the dropdown if the API is slow, and so tests can
// reference the canonical list.
export const DEFAULT_SOLUTION_TYPES = [
  "Pipe Spool",
  "Manifold",
  "Skid",
  "Rack",
  "Header",
  "Stainless",
  "Custom Fab",
] as const;

// 17-stage spool workflow ported from the prototype. The 8 welding
// bays are alternatives, not sequential — most spools weld in just
// one bay and the other seven sit at status='skipped' (or are never
// touched at all, since stage rows are lazy-seeded). Colours come
// from the prototype palette.
export interface StageDef {
  key: string;
  name: string;
  short: string;
  color: string;
  // false only for Dispatched. Lets the UI hide the hours input on
  // stages that aren't time-tracked.
  hasHours: boolean;
  // weld === true for any of the 8 welding bays so the Gantt and
  // auto-schedule know they are alternatives.
  weld?: boolean;
  bay?: number;
}

export const STAGES: readonly StageDef[] = [
  { key: "takeoff",     name: "Take-off",     short: "TO",  color: "#7C3AED", hasHours: true },
  { key: "picking",     name: "Pipe Picking", short: "PP",  color: "#0284C7", hasHours: true },
  { key: "cutting",     name: "Cutting",      short: "CUT", color: "#C77700", hasHours: true },
  { key: "rollgroove",  name: "Roll Grooving", short: "RG", color: "#0F766E", hasHours: true },
  { key: "threading",   name: "Threading",    short: "TH",  color: "#92400E", hasHours: true },
  { key: "kitting",     name: "Kitting",      short: "KIT", color: "#BE185D", hasHours: true },
  { key: "weldbay1",    name: "Welding Bay 1", short: "W1", color: "#1B449C", hasHours: true, weld: true, bay: 1 },
  { key: "weldbay2",    name: "Welding Bay 2", short: "W2", color: "#0284C7", hasHours: true, weld: true, bay: 2 },
  { key: "weldbay3",    name: "Welding Bay 3", short: "W3", color: "#0F766E", hasHours: true, weld: true, bay: 3 },
  { key: "weldbay4",    name: "Welding Bay 4", short: "W4", color: "#7C3AED", hasHours: true, weld: true, bay: 4 },
  { key: "weldbay5",    name: "Welding Bay 5", short: "W5", color: "#C77700", hasHours: true, weld: true, bay: 5 },
  { key: "weldbay6",    name: "Welding Bay 6", short: "W6", color: "#2E7D32", hasHours: true, weld: true, bay: 6 },
  { key: "weldbay7",    name: "Welding Bay 7", short: "W7", color: "#BE185D", hasHours: true, weld: true, bay: 7 },
  { key: "weldbay8",    name: "Welding Bay 8", short: "W8", color: "#92400E", hasHours: true, weld: true, bay: 8 },
  { key: "passivation", name: "Passivation",  short: "PV",  color: "#0F766E", hasHours: true },
  { key: "ndt",         name: "NDT",          short: "NDT", color: "#C77700", hasHours: true },
  { key: "dispatched",  name: "Dispatched",   short: "DSP", color: "#2E7D32", hasHours: false },
] as const;

export const STAGE_KEYS = STAGES.map((s) => s.key) as readonly string[];
export type StageKey = (typeof STAGES)[number]["key"];

// Reserved key inside productionJobs.nominalStageHours holding the
// job's single Welding labour budget. The 8 welding bays are
// alternatives (a spool welds in exactly one), so the nominal form
// shows one "Welding" field and the distributor writes it to each
// spool's chosen bay. Custom stage keys are auto-slugged to
// [a-z0-9-] so an underscore-wrapped sentinel can never collide.
export const WELD_NOMINAL_KEY = "__weld__";

export const STAGE_STATUSES = ["pending", "in-progress", "complete", "skipped"] as const;
export type StageStatus = (typeof STAGE_STATUSES)[number];

export const STAGE_BY_KEY: Record<string, StageDef> = Object.fromEntries(
  STAGES.map((s) => [s.key, s]),
);

export function isStageKey(s: string): s is StageKey {
  return s in STAGE_BY_KEY;
}

export function isStageStatus(s: string): s is StageStatus {
  return (STAGE_STATUSES as readonly string[]).includes(s);
}

// Auto-schedule cascade assumes one bay/welder works a single 8h
// shift per business day. Calendar is naive — no holidays, no
// weekend skip — which matches the prototype's simple cascade.
export const HOURS_PER_DAY = 8;
